import { sanitizeURL } from '../shared/utils/sanitize';
import { fetchChatResponse } from './api';
import {
  appendInlineCalendlyWidget,
  collapseWidget,
  detectBookingIntent,
  restoreChatMessages,
  showCalendlyLoadingMessage,
} from './booking-helpers';
import {
  CalendlyEventData,
  fetchWidgetConfig,
  initCalendlyInline,
  loadCalendlyScript,
  logMeetingBooking,
} from './calendly';
import {
  getPrimaryMessageWrapper,
  recieveAndAppendMessage,
  sendAndAppendMessage,
  showDots,
} from './message-helpers';
import { templateTextWidget } from './template';
import { createToken } from './token';
import { MainWindow, Nullable, TextChatUIElements } from './types';

const widget = document.createElement('template');
widget.innerHTML = templateTextWidget;

class WitzoChatElement extends HTMLElement {
  /**Url to connect to the webhook */
  url: Nullable<string> = null;
  /**Reference for elements */
  chat: Nullable<TextChatUIElements> = null;

  default_primary_text: Nullable<string> = null;

  bot_logo_color: Nullable<string> = ``;

  user_chat_color: Nullable<string> = ``;

  send_button_color: Nullable<string> = ``;

  floating_button_color: Nullable<string> = ``;

  uniqueId: Nullable<string> = null;

  banner_TextParagraph: Nullable<string> = null;

  banner_TextParagraphColor: Nullable<string> = null;

  /**Calendly URL for meeting booking */
  private calendlyUrl: Nullable<string> = null;

  /**Session ID for tracking */
  private sessionId: string = '';

  /**Is Initialized */
  isInitialized: boolean = false;
  private _mainWindow: MainWindow;

  private successfulChatCount: number;
  private date: Date;
  private flags: {
    isOpen: boolean;
    isButtonClicked: boolean;
    isAutoOpen: boolean;
    defaultMessageDisplayed: boolean;
  } = {
    isButtonClicked: false,
    isOpen: false,
    isAutoOpen: false,
    defaultMessageDisplayed: false,
  };

  /**User IP Address */
  userIp: Nullable<string> = null;

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'closed' });
    root.appendChild(node);

    this._mainWindow = {
      back: () => {},
      close: () => {},
    };

    this.getElements(root);
    this.successfulChatCount = 0;
    this.date = new Date();

    const date = sessionStorage.getItem('witzo_chat_date');
    if (date) {
      this.date = new Date(date);
    } else {
      sessionStorage.setItem('witzo_chat_date', new Date().toISOString());
    }

    const chatCount = sessionStorage.getItem('witzo_chat_count');
    if (chatCount) this.successfulChatCount = Number(chatCount);
    else sessionStorage.setItem('witzo_chat_count', `0`);

    // Initialize as null instead of empty string
    this.default_primary_text = null;
    this.bot_logo_color = ``;
    this.send_button_color = '';
    this.floating_button_color = '';
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const query = <T extends HTMLElement>(selctor: string) => root.querySelector(selctor) as T;
    try {
      this.chat = {
        // backButton: query('#backToSelection'),
        bannerText: query('#banner-text'),
        logoIcon: query('#logoIcon'),
        closeIcon: query('#close-button-icon'),
        chatHeader: query('#chat-header'),
        chatWindow: query('#textChatWidget'),
        closeButton: query('#closeTextChat'),
        sendButton: query('#textSendButton'),
        messageInput: query('#textMessageInput'),
        messagesContainer: query('#textMessagesArea'),
        sendButtonRect: root.querySelector('#send-button-color') as SVGRectElement,
        floatingButton: query('#floating-btn'),
        bannerTextParagraph: query('#banner-text-paragraph'),
        calendlyButton: query('#calendlyButton'),
        calendlyContainer: query('#calendlyContainer'),
        calendlyCloseButton: query('#calendlyCloseButton'),
        calendlyWidgetWrapper: query('#calendlyWidgetWrapper'),
      };
      this.isInitialized = true;
    } catch (err) {
      this.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description Parse markdown text to HTML
   */
  private parseMarkdownToHTML(text: string): string {
    if (!text || text.trim() === '') {
      console.warn('Empty text passed to parseMarkdownToHTML');
      return '';
    }

    // Normalize newlines - handle multiple formats:
    // HTML attributes store \n as literal two-character string (backslash + n)
    // We need to convert these to actual newline characters
    let normalizedText = text;

    // First, handle the literal string \n (from HTML attributes)
    // In JavaScript regex, we need to match a backslash followed by 'n'
    // A backslash in regex is \\, so \\n matches literal \n
    normalizedText = normalizedText.replace(/\\n/g, '\n');

    // Also handle \r\n (Windows line endings)
    normalizedText = normalizedText.replace(/\r\n/g, '\n');

    // Collapse excessive blank lines (3 or more newlines become 2)
    normalizedText = normalizedText.replace(/\n{3,}/g, '\n\n');

    const lines = normalizedText.split('\n');

    const out: string[] = [];
    let inUL = false;
    let inOL = false;

    const flushLists = () => {
      if (inUL) {
        out.push('</ul>');
        inUL = false;
      }
      if (inOL) {
        out.push('</ol>');
        inOL = false;
      }
    };

    const formatInline = (s: string) => {
      // links [text](url) with URL sanitization
      s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
        const safeUrl = sanitizeURL(url);
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      });
      // **bold**
      s = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return s;
    };

    for (let raw of lines) {
      const line = raw.trim();

      // Blank line -> close any open list and add a small spacer
      if (line === '') {
        flushLists();
        continue;
      }

      // Check for heading-style lines (lines that are entirely bold)
      // Match lines like "** What I Can Help With..**" or "**Note:**"
      const headingMatch = line.match(/^\*\*\s*(.+?)\s*:?\s*\.{0,2}\*\*$/);
      if (headingMatch) {
        flushLists();
        out.push(`<p><strong>${headingMatch[1]}</strong></p>`);
        continue;
      }

      // Numbered list "1. item"
      const olMatch = line.match(/^(\d+)\.\s+(.*)$/);
      if (olMatch) {
        const item = formatInline(olMatch[2]);
        if (inUL) {
          out.push('</ul>');
          inUL = false;
        }
        if (!inOL) {
          out.push('<ol>');
          inOL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Bulleted list "- item"
      const ulMatch = line.match(/^-\s+(.*)$/);
      if (ulMatch) {
        const item = formatInline(ulMatch[1]);
        if (inOL) {
          out.push('</ol>');
          inOL = false;
        }
        if (!inUL) {
          out.push('<ul>');
          inUL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Standalone label lines ending with ":" that aren't already bold headings
      // Match lines like "Note:" or "Tip:" but NOT "**Note:**" (already handled above)
      const labelMatch = line.match(/^(?!\*\*)(.+?):\s*$/);
      if (labelMatch && !line.includes('**')) {
        flushLists();
        // preserve any inline **bold** within the label too
        const labelText = formatInline(labelMatch[1]);
        out.push(`<p><strong>${labelText}:</strong></p>`);
        continue;
      }

      // Normal paragraph
      flushLists();
      out.push(`<p>${formatInline(line)}</p>`);
    }

    flushLists();

    // Final tidy-up: remove accidental double breaks or empty paragraphs
    return out
      .join('')
      .replace(/(<br>\s*){2,}/g, '<br>')
      .replace(/<p>\s*<\/p>/g, '');
  }

  /**
   * @description Process and display the default primary message
   */
  private processAndDisplayMessage() {
    // Prevent duplicate display
    if (this.flags.defaultMessageDisplayed) {
      return;
    }

    if (!this.chat?.messagesContainer) {
      console.warn('Messages container not available');
      return;
    }

    if (!this.default_primary_text || this.default_primary_text.trim() === '') {
      console.warn('No default primary text to process');
      return;
    }

    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );

    const formattedContent = this.parseMarkdownToHTML(this.default_primary_text);
    const htmlWrapped = `<div class="md-content">${formattedContent}</div>`;

    const getText = async () => ({
      content: htmlWrapped,
      isHtml: true, // make sure your renderer uses innerHTML when this is true
    });
    recieveAndAppendMessage(wrapper, this.chat!.messagesContainer, getText());

    // Mark as displayed to prevent duplicates
    this.flags.defaultMessageDisplayed = true;
  }

  /**
   * @description external function to set the url to connect to web-hook
   */
  set chatUrl(url: string) {
    this.url = url;
  }

  set mainWindow(_mainWindow: MainWindow) {
    this._mainWindow = _mainWindow;
  }

  set defaultPrimaryText(text: string) {
    this.default_primary_text = text;

    // If elements aren't ready yet, the text will be processed in connectedCallback
    if (!this.isInitialized || !this.chat?.messagesContainer) {
      return;
    }

    this.processAndDisplayMessage();
  }

  set botBackgroundColor(color: string) {
    this.bot_logo_color = color;
  }

  set sendBackgroundColor(color: string) {
    this.send_button_color = color;
    if (this.chat) this.chat.sendButtonRect.style.fill = color;
  }

  set floatingButtonColor(color: string) {
    this.floating_button_color = color;
    if (this.chat?.floatingButton) this.chat.floatingButton.style.backgroundColor = color;
  }

  set bannerText(text: string) {
    if (this.chat?.bannerText) this.chat.bannerText.textContent = text;
  }

  set chatHeader(backgroundColor: string) {
    if (this.chat?.chatHeader) this.chat.chatHeader.style.backgroundColor = backgroundColor;
    if (this.chat?.closeButton) this.chat.closeButton.style.backgroundColor = backgroundColor;
  }

  set logoIcon(iconUrl: string) {
    if (this.chat?.logoIcon) this.chat.logoIcon.src = iconUrl;
  }

  set autoOpen(isAutoOpen: boolean) {
    this.flags.isAutoOpen = isAutoOpen;
  }

  set bannerTextColor(colorValue: string) {
    if (this.chat?.bannerText) this.chat.bannerText.style.color = colorValue;
  }

  set closeButtonColor(colorValue: string) {
    if (this.chat?.closeIcon) this.chat.closeIcon.style.fill = colorValue;
  }

  set userChatColor(colorValue: string) {
    this.user_chat_color = colorValue;
  }

  set uniqueID(uniqueId: string) {
    this.uniqueId = uniqueId;
  }

  set bannerTextParagraph(text: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.textContent = text;
  }

  set bannerTextParagraphColor(colorValue: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.style.color = colorValue;
  }

  async handleResponse(text: string) {
    if (!text || !this.url) return;
    if (!this.chat) return;

    const sessionId = createToken();

    sendAndAppendMessage(
      this.chat.messagesContainer,
      this.chat.messageInput,
      text,
      this.user_chat_color ?? '#d01137ff'
    );
    const response = await fetchChatResponse(
      this.url,
      sessionId,
      text,
      this.successfulChatCount,
      this.uniqueId ?? '',
      this.userIp
    );
  }

  async fetchUserIp() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      this.userIp = data.ip;
    } catch (error) {
      console.warn('Failed to fetch user IP:', error);
    }
  }

  async connectedCallback() {
    this.bindEvents();
    await this.fetchUserIp();

    // Initialize session ID
    this.sessionId = createToken();

    // Initialize Calendly
    await this.initializeCalendly();

    // Process any pending default text after connection and initialization
    if (this.isInitialized && this.default_primary_text) {
      this.processAndDisplayMessage();
    }

    setTimeout(() => {
      const wasClosedByUser = localStorage.getItem('witzo_widget_closed') === 'true';
      if (this.flags.isAutoOpen === true && this.flags.isButtonClicked === false && !wasClosedByUser) {
        this.toggleButton();
      }
    }, 5000);

    setTimeout(() => {
      this.chat?.floatingButton.classList.remove('hidden');
    }, 2000);
  }

  bindEvents() {
    if (!this.chat || this.isInitialized === false) return;
    const {
      // backButton,
      closeButton,
      sendButton,
      messageInput,
      messagesContainer,
      chatWindow,
      floatingButton,
      calendlyButton,
      calendlyCloseButton,
    } = this.chat;

    floatingButton.addEventListener('click', () => {
      this.toggleButton();
    });

    // backButton.addEventListener('click', () => {
    //   this._mainWindow.back();
    //   this.chat?.chatWindow.classList.add('hidden');
    // });

    closeButton.addEventListener('click', () => {
      this._mainWindow.close();
      this.chat?.chatWindow.classList.add('hidden');
      this.flags.isOpen = false;
      // Remember that user closed the widget so it won't auto-open again
      localStorage.setItem('witzo_widget_closed', 'true');
    });

    // Calendly button handlers
    calendlyButton.addEventListener('click', () => {
      this.openCalendly();
    });

    calendlyCloseButton.addEventListener('click', () => {
      this.closeCalendly();
    });

    const gapRequired = 2 * 60 * 1000;
    sendButton.addEventListener('click', async () => {
      if (!this.url) return;
      const text = this.chat!.messageInput.value.trim();

      const gap = new Date().getTime() - this.date.getTime();
      if (gap > gapRequired) {
        this.successfulChatCount = 0;
        this.date = new Date();
      }

      if (text !== '') {
        sendAndAppendMessage(
          messagesContainer,
          messageInput,
          text,
          this.user_chat_color ?? '#d01137ff'
        );

        // Check if user wants to book a meeting
        const hasBookingIntent = detectBookingIntent(text);

        if (hasBookingIntent && this.calendlyUrl && this.chat) {
          // Show loading message
          showCalendlyLoadingMessage(messagesContainer);

          // Wait a bit for better UX
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Show inline Calendly widget with widget expansion
          const widgetElement = this.chat.chatWindow;

          if (widgetElement) {
            // Define skip handler to restore chat
            const handleSkip = () => {
              // Collapse widget and restore chat
              collapseWidget(widgetElement, messagesContainer);
              restoreChatMessages(messagesContainer);
            };

            appendInlineCalendlyWidget(
              messagesContainer,
              this.calendlyUrl,
              widgetElement,
              handleSkip
            );
          } else {
            console.error('[Booking] Widget element not found');
          }

          return; // Don't send to webhook
        }

        const wrapper = showDots(messagesContainer, this.bot_logo_color ?? '#fc0e3f');
        const sessionId = createToken();
        const response = fetchChatResponse(
          this.url,
          sessionId,
          text,
          this.successfulChatCount,
          this.uniqueId ?? '',
          this.userIp
        );

        this.successfulChatCount += 1;
        sessionStorage.setItem('witzo_chat_count', `${this.successfulChatCount}`);

        await recieveAndAppendMessage(wrapper, messagesContainer, response);
      }
    });

    messageInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendButton.click();
      }
    });
  }

  /**
   * @description toggle button for floating button
   */
  toggleButton() {
    if (!this.chat) return;
    this.flags.isButtonClicked = true;
    this.flags.isOpen = !this.flags.isOpen;
    this.flags.isOpen ? this.maximizeWidget() : this.minimizeWidget();
  }

  /**Minimises the chat window */
  minimizeWidget() {
    if (!this.chat) return;

    // Remember that user closed the widget so it won't auto-open again
    localStorage.setItem('witzo_widget_closed', 'true');

    this.chat.chatWindow.classList.add('minimizing');
    setTimeout(() => {
      this.chat?.chatWindow.classList.add('hidden');
      this.chat?.chatWindow.classList.remove('minimizing');
      //this.chat?.openButton.classList.remove('hidden');
    }, 300);
  }

  /**Maximize the chat window */
  maximizeWidget() {
    if (!this.chat) return;

    //this.chat.openButton.classList.add('hidden');
    this.chat.chatWindow.classList.remove('hidden');
    this.chat.messageInput.focus();
  }

  /**
   * Initialize Calendly integration
   */
  private async initializeCalendly() {
    if (!this.url || !this.uniqueId) return;

    try {
      // Extract API base URL from webhook URL
      const apiBaseUrl = new URL(this.url).origin + '/api/v1';

      // Fetch widget configuration
      const config = await fetchWidgetConfig(apiBaseUrl, this.uniqueId);

      if (config.has_calendly && config.calendly_url) {
        this.calendlyUrl = config.calendly_url;

        // Show Calendly button
        this.chat?.calendlyButton.classList.remove('hidden');

        await loadCalendlyScript();

        // Setup Calendly event listener
        this.setupCalendlyEventListener();
      }
    } catch (error) {
      console.error('Failed to initialize Calendly:', error);
    }
  }

  /**
   * Open Calendly booking interface
   */
  private openCalendly() {
    if (!this.calendlyUrl || !this.chat) return;

    // Show Calendly container
    this.chat.calendlyContainer.classList.add('active');

    // Clear previous widget
    this.chat.calendlyWidgetWrapper.innerHTML = '';

    // Initialize Calendly inline widget
    initCalendlyInline(this.chat.calendlyWidgetWrapper, this.calendlyUrl);
  }

  /**
   * Close Calendly booking interface
   */
  private closeCalendly() {
    if (!this.chat) return;

    this.chat.calendlyContainer.classList.remove('active');
  }

  /**
   * Setup Calendly event listeners for tracking bookings
   */
  private setupCalendlyEventListener() {
    window.addEventListener('message', (e: MessageEvent) => {
      if (e.origin !== 'https://calendly.com') return;

      const data: CalendlyEventData = e.data;

      // Event scheduled successfully
      if (data.event === 'calendly.event_scheduled') {
        this.handleCalendlyEventScheduled(data);
      }
    });
  }

  /**
   * Handle Calendly event scheduled
   */
  private async handleCalendlyEventScheduled(data: CalendlyEventData) {
    if (!this.chat || !this.url || !this.uniqueId) return;

    // Close Calendly interface
    this.closeCalendly();

    // Collapse widget back to normal size and restore chat
    const widgetElement = this.chat.chatWindow;
    if (widgetElement) {
      collapseWidget(widgetElement, this.chat.messagesContainer);
      restoreChatMessages(this.chat.messagesContainer);
    }

    // Show confirmation message in chat
    const confirmationMessage =
      'Meeting booked successfully! You will receive a confirmation email shortly.';
    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );
    recieveAndAppendMessage(
      wrapper,
      this.chat.messagesContainer,
      Promise.resolve({ content: confirmationMessage })
    );

    // Log meeting to backend
    try {
      const apiBaseUrl = new URL(this.url).origin + '/api/v1';
      const eventUri = data.payload?.event?.uri || '';
      const inviteeUri = data.payload?.invitee?.uri || '';

      await logMeetingBooking(apiBaseUrl, {
        session_id: this.sessionId,
        widget_key: this.uniqueId,
        event_uri: eventUri,
        invitee_uri: inviteeUri,
      });
    } catch (error) {
      console.error('Failed to log meeting:', error);
    }
  }
}

export { WitzoChatElement };
